import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Lightbox } from 'ngx-lightbox';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import ProductAsset from 'src/app/model/product-asset';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import Product from 'src/app/model/product/product';
import {
  SubProject,
  SUB_PROJECT_TYPES
} from 'src/app/model/project/sub-project';
import RegisteredAction from 'src/app/model/registered-action';
import { REGISTERED_ACTION_TYPES } from 'src/app/model/registered-action-type';
import { AuthService } from 'src/app/services/auth.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { FilePickerComponent } from 'src/app/shared/project-components/helpers/file-picker/file-picker.component';
import { ClientSignSelectorHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/client-sign-selector/client-sign-selector-helper';
import { StyleWithIndex } from 'src/app/shared/project-components/helpers/ip-board-design-picker/ip-board-design-picker.component';
import { User } from 'src/app/model/user';
import {
  SubProjectIdStatusPair,
  ToggleImmoSubProjectExecutedControllerRequestService,
  ToggleImmoSubProjectExecutedResponse
} from 'src/app/services/api/toggle-immo-sub-project-executed/toggle-immo-sub-project-executed-controller-request.service';
import { AtelierRelevantAndConsideredCompleteControllerRequestService } from 'src/app/services/api/atelier/atelier-relevant-and-considered-complete-controller-request.service';
import { Project } from 'src/app/model/project/project';
import { SnackService } from 'src/app/services/snack.service';
import { BooleanHelper } from 'src/app/helpers/boolean-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { ModalService } from 'src/app/services/modal.service';

@Component({
  selector: 'ip-board-executed',
  templateUrl: './ip-board-executed.component.html',
  styleUrls: ['./ip-board-executed.component.scss']
})
export class IPBoardExecutedComponent extends SubscriptionBasedComponent
  implements OnInit {
  @Input()
  project: Project;

  @Input()
  subProject: SubProject;

  @Input()
  products: Product[];

  @Output()
  save = new EventEmitter<void>();

  firstIPBoardProduct: IPBoard;
  SUB_PROJECT_TYPES = SUB_PROJECT_TYPES;
  installerFeedback: string = null;
  executed: boolean = false;
  placementDoneRegistration: RegisteredAction;
  executionAssets: StyleWithIndex[];
  user: User;

  @ViewChild(FilePickerComponent, { static: false })
  filePickerComponent: FilePickerComponent;

  submitting: boolean = false;

  constructor(
    private authService: AuthService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private toggleImmoSubProjectExecutedControllerRequestService: ToggleImmoSubProjectExecutedControllerRequestService,
    private atelierRelevantAndConsideredCompleteControllerRequestService: AtelierRelevantAndConsideredCompleteControllerRequestService,
    private _lightbox: Lightbox,
    private snackService: SnackService,
    private modalService: ModalService
  ) {
    super();
  }

  ngOnInit() {
    this.initializeSubscriptions();

    const IPBoardProducts = this.products.filter(x =>
      ProductHelper.isIPBoard(x)
    );
    this.firstIPBoardProduct =
      IPBoardProducts != null && IPBoardProducts.length > 0
        ? IPBoardProducts[0]
        : null;
    this.executed = ProjectHelper.getSubProjectNullableExecutionState(
      this.subProject,
      this.project
    );
    this.initInstallerFeedback();
    this.initRegisteredAction();
    this.calculateExecutionAssets();
  }

  private initializeSubscriptions(): void {
    this.addSubscription(
      this.authService.getAuthenticatedUserSubscribable(),
      user => {
        this.user = user;
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProjectPlacementExecutedSubscribable(),
      (subProjectId: number) => {
        if (this.subProject.id != null && subProjectId === this.subProject.id) {
          this.evaluateExecuted();
        }
      }
    );
  }

  initInstallerFeedback() {
    if (this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (this.firstIPBoardProduct != null) {
        this.installerFeedback = this.firstIPBoardProduct.productPlacement.installerFeedback;
      }
    } else {
      this.installerFeedback = this.subProject.projectPlacement.placerFeedback;
    }
  }

  initRegisteredAction() {
    if (
      this.subProject.projectPlacement != null &&
      (this.subProject.projectPlacement.placementDone ||
        (this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
          this.executed)) &&
      this.subProject.projectPlacement.placementDoneRegistration != null
    ) {
      this.placementDoneRegistration = this.subProject.projectPlacement.placementDoneRegistration;
    } else {
      const stopExecutions =
        this.subProject.projectPlacement != null &&
        this.subProject.projectPlacement.registeredActions != null
          ? this.subProject.projectPlacement.registeredActions.filter(
              x =>
                x.type.ordinal ===
                REGISTERED_ACTION_TYPES.STOP_PROJECT_EXECUTION.ordinal
            )
          : null;
      const sortedActions =
        stopExecutions != null && stopExecutions.length > 0
          ? stopExecutions.length > 1
            ? stopExecutions.sort(
                (a, b) =>
                  new Date(b.date).getTime() - new Date(a.date).getTime()
              )
            : stopExecutions
          : [null];
      this.placementDoneRegistration = sortedActions[0];
    }
  }

  executedChanged(): void {
    if (!this.executed) {
      this.askExecutedChangedConfirmation();
    } else {
      this.continueExecutedChanged();
    }
  }

  private async askExecutedChangedConfirmation(): Promise<void> {
    const atelierRelevantAndConsideredComplete = await this.atelierRelevantAndConsideredCompleteControllerRequestService.getAtelierRelevantAndConsideredCompleteForBaseProjectOfProject(
      this.project.id
    );
    if (atelierRelevantAndConsideredComplete) {
      this.modalService.confirmDialog(
        'Uitgevoerd',
        'OPGELET! Magazijn stock werd reeds afgetrokken voor deze job. Bent u zeker dat u wil doorgaan met deze actie? Indien ja, gelieve stock aantallen te corrigeren',
        (answer: boolean) => {
          if (answer) {
            this.continueExecutedChanged();
          } else {
            this.executed = true;
          }
        }
      );
    } else {
      this.continueExecutedChanged();
    }
  }

  private continueExecutedChanged(): void {
    this.submitting = true;
    this.toggleImmoSubProjectExecutedControllerRequestService
      .toggleImmoSubProjectExecuted(this.subProject.id)
      .then((res: ToggleImmoSubProjectExecutedResponse) => {
        this.processToggleExecutedResponse(res);
        this.toggleComponentExecutionState(res);
        this.emitSubProjectStatusChanges(res);
        this.snackService.showSuccess('Uitgevoerd succesvol aangepast');
      })
      .finally(() => (this.submitting = false));
  }

  private processToggleExecutedResponse(
    res: ToggleImmoSubProjectExecutedResponse
  ): void {
    this.placementDoneRegistration = res.placementDoneRegistration;
    this.subProject.currentExecutionDate = res.currentExecutionDate;
    this.subProject.projectPlacement.placementDoneRegistration =
      res.placementDoneRegistration;
    if (CollectionHelper.isPopulated(res.subProjectIdWithNewStatus)) {
      res.subProjectIdWithNewStatus.forEach(p => {
        if (p.subProjectId === this.subProject.id) {
          this.subProject.status = p.newStatus;
        } else {
          const otherSubProject = CollectionHelper.isPopulated(
            this.project.subProjects
          )
            ? this.project.subProjects.find(sp => sp.id === p.subProjectId)
            : null;
          if (otherSubProject != null) {
            otherSubProject.status = p.newStatus;
          }
        }
      });
    }
  }

  private toggleComponentExecutionState(
    res: ToggleImmoSubProjectExecutedResponse
  ): void {
    const newExecutedValue = BooleanHelper.invertTrueToNull(
      ProjectHelper.getSubProjectNullableExecutionState(
        this.subProject,
        this.project
      )
    );
    if (this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (
        this.project != null &&
        CollectionHelper.isPopulated(this.project.products)
      ) {
        this.project.products.forEach(p => {
          if (p.productPlacement != null) {
            p.productPlacement.executionComplete = newExecutedValue;
            p.productPlacement.executionDate = res.currentExecutionDate;
            p.productPlacement.executionTime = res.executionTime;
          }
        });
      }
    } else {
      if (this.subProject.projectPlacement != null) {
        this.subProject.projectPlacement.placementDone = newExecutedValue;
      }
    }
  }

  private emitSubProjectStatusChanges(
    res: ToggleImmoSubProjectExecutedResponse
  ): void {
    if (!CollectionHelper.isPopulated(res.subProjectIdWithNewStatus)) {
      return;
    }
    res.subProjectIdWithNewStatus.forEach((p: SubProjectIdStatusPair) => {
      this.projectDisplayObservableService
        .getSubProjectStatusChangedSubscribable()
        .next({
          subProjectId: p.subProjectId,
          status: p.newStatus
        });
    });
  }

  executedFeedbackChanged(feedback: string): void {
    if (this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (this.firstIPBoardProduct != null) {
        this.firstIPBoardProduct.productPlacement.installerFeedback = feedback;
      }
    } else {
      this.subProject.projectPlacement.placerFeedback = feedback;
    }
    this.save.emit();
  }

  calculateExecutionAssets(): void {
    this.executionAssets = [];
    if (this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (this.firstIPBoardProduct != null) {
        this.executionAssets = this.firstIPBoardProduct.productAssets
          .map((pa, i) => {
            return {
              style: ClientSignSelectorHelper.getBackgroundImageStyle(
                `/api/assets/${pa.asset.uuid}`
              ),
              productAsset: pa,
              index: i
            };
          })
          .filter(
            x =>
              !x.productAsset.archived &&
              x.productAsset.formSection === 'execution'
          );
      }
    } else {
      this.executionAssets = this.subProject.projectPlacement.projectPlacementAssets
        .map((pa, i) => {
          return {
            style: ClientSignSelectorHelper.getBackgroundImageStyle(
              `/api/assets/${pa.asset.uuid}`
            ),
            productAsset: pa,
            index: i
          };
        })
        .filter(
          x =>
            !x.productAsset.archived &&
            x.productAsset.formSection === 'adjustmentPlacementSuccess'
        );
    }
  }

  openLightbox(productAsset: ProductAsset): void {
    this._lightbox.open([
      {
        src: `/api/assets/${productAsset.asset.uuid}`,
        thumb: `/api/assets/${productAsset.asset.uuid}`
      }
    ]);
  }

  deleteExecutionAsset($event: MouseEvent, style: StyleWithIndex): void {
    $event.stopPropagation();
    this.filePickerComponent.removeFile(style.productAsset, style.index);
  }

  private evaluateExecuted(): void {
    if (this.subProject.type !== SUB_PROJECT_TYPES.SOLDRENTED) {
      return;
    }
    this.executed =
      this.subProject.projectPlacement != null &&
      this.subProject.projectPlacement.placementDone;
  }
}
